/**
 * Phase 17 — bulk UPDATE of existing cases dialog.
 *
 * Pairs with BulkImportCasesDialog (which CREATES). This one applies
 * patches to cases that already exist by matching on `case_id`. UX:
 *   1. "Download blank template" or "Download current data" (optional)
 *   2. Choose file → dry-run preview (per-row diff + totals)
 *   3. Confirm → execute → show applied + any post-execution errors
 *
 * Empty cell = don't touch; `__CLEAR__` literal = explicit clear.
 * HR-admin only.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  Loader2,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import api, { describeApiError } from "../../lib/api";

const EDITABLE_COLUMNS = [
  "status",
  "outcome",
  "investigation_end_date",
  "date_of_discussion",
  "date_reported",
  "reported_by",
  "case_details",
  "follow_up_required",
  "follow_up_date",
  "follow_up_notes",
  "assigned_hr_partner_id",
  "assigned_hr_partner_email",
];

export default function BulkUpdateCasesDialog({
  open,
  onOpenChange,
  onUpdateComplete,
}) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setApplied(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const downloadTemplate = async (kind) => {
    try {
      const path =
        kind === "blank"
          ? "/admin/cases/bulk-update/template.csv"
          : "/admin/cases/bulk-update/current.csv";
      const resp = await api.get(path, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        kind === "blank"
          ? "bulk-update-template.csv"
          : "bulk-update-current.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Select a CSV file first.");
      return;
    }
    setPreviewing(true);
    setPreview(null);
    setApplied(null);
    try {
      const form = new FormData();
      form.append("csv", file);
      const resp = await api.post(
        "/admin/cases/bulk-update?dry_run=true",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(resp.data);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const runApply = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const form = new FormData();
      form.append("csv", file);
      const resp = await api.post(
        "/admin/cases/bulk-update?dry_run=false",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setApplied(resp.data);
      toast.success(
        `Applied ${resp.data?.would_update ?? 0} updates to existing cases.`
      );
      if (onUpdateComplete) onUpdateComplete();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const previewRows = (preview?.per_row_changes || []).slice(0, 20);
  const errors = preview?.errors || [];
  const warnings = preview?.warnings || [];
  const canApply =
    preview?.status === "preview_ok" &&
    (preview?.would_update ?? 0) > 0 &&
    !applied;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="bulk-update-cases-dialog"
      >
        <DialogHeader>
          <DialogTitle>Bulk update existing cases</DialogTitle>
          <DialogDescription>
            Match by <code>case_id</code>; empty cells = don't touch; literal{" "}
            <code>__CLEAR__</code> = explicit clear. Reuses the same validation
            as single-case edits. All updates are audit-logged. Emails are
            suppressed for bulk changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Template downloads */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("blank")}
              data-testid="bulk-update-download-blank-btn"
            >
              <Download className="mr-1 h-4 w-4" />
              Blank template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("current")}
              data-testid="bulk-update-download-current-btn"
            >
              <FileSpreadsheet className="mr-1 h-4 w-4" />
              Current data export
            </Button>
            <span className="text-xs text-muted-foreground self-center">
              Edit in Excel, re-upload here.
            </span>
          </div>

          {/* File picker */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Choose edited CSV
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setPreview(null);
                setApplied(null);
              }}
              data-testid="bulk-update-file-input"
              className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm hover:file:bg-accent"
            />
            {file && (
              <p className="text-xs text-muted-foreground">
                {file.name} · {Math.round(file.size / 1024)} KB
              </p>
            )}
          </div>

          {/* Preview button */}
          {!applied && (
            <div>
              <Button
                onClick={runPreview}
                disabled={!file || previewing}
                data-testid="bulk-update-preview-btn"
              >
                {previewing ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Previewing…
                  </>
                ) : (
                  <>
                    <Upload className="mr-1 h-4 w-4" />
                    Dry-run preview
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Preview result */}
          {preview && !applied && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span data-testid="bulk-update-preview-status">
                  Status:{" "}
                  <strong>
                    {preview.status === "preview_ok"
                      ? "Ready to apply"
                      : "Rejected"}
                  </strong>
                </span>
                <span data-testid="bulk-update-preview-counts">
                  Would update <strong>{preview.would_update}</strong> ·{" "}
                  Matched <strong>{preview.matched}</strong> · Unmatched{" "}
                  <strong>{preview.unmatched}</strong>
                </span>
              </div>

              {errors.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive flex items-center">
                    <AlertCircle className="mr-1 h-4 w-4" />
                    {errors.length} error{errors.length === 1 ? "" : "s"} —
                    nothing will be written
                  </p>
                  <ul className="list-disc pl-6 text-xs text-destructive space-y-0.5">
                    {errors.slice(0, 20).map((e, i) => (
                      <li key={i} data-testid={`bulk-update-error-${i}`}>
                        Row {e.row}
                        {e.field ? ` · ${e.field}` : ""}: {e.reason}
                      </li>
                    ))}
                    {errors.length > 20 && (
                      <li className="list-none text-muted-foreground">
                        … and {errors.length - 20} more
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {warnings.length > 0 && (
                <ul className="list-disc pl-6 text-xs text-amber-600 space-y-0.5">
                  {warnings.map((w, i) => (
                    <li key={i}>
                      Row {w.row}
                      {w.field ? ` · ${w.field}` : ""}: {w.reason}
                    </li>
                  ))}
                </ul>
              )}

              {previewRows.length > 0 && (
                <div className="overflow-x-auto">
                  <p className="text-xs font-medium mb-1">
                    Diff preview (first {previewRows.length} rows)
                  </p>
                  <Table
                    className="text-xs"
                    data-testid="bulk-update-diff-table"
                  >
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Case</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Before</TableHead>
                        <TableHead>→</TableHead>
                        <TableHead>After</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewRows.flatMap((pc) =>
                        Object.keys(pc.after || {}).map((k) => (
                          <TableRow
                            key={`${pc.row}-${k}`}
                            data-testid={`bulk-update-diff-${pc.row}-${k}`}
                          >
                            <TableCell>{pc.row}</TableCell>
                            <TableCell className="font-mono">
                              {pc.case_id}
                            </TableCell>
                            <TableCell>{k}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatCell(pc.before?.[k])}
                            </TableCell>
                            <TableCell>
                              <ArrowRight className="h-3 w-3" />
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCell(pc.after?.[k])}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Applied result */}
          {applied && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm"
              data-testid="bulk-update-applied-result"
            >
              <p className="font-medium text-emerald-800 flex items-center">
                <CheckCircle2 className="mr-1 h-4 w-4" />
                Applied {applied.would_update} update
                {applied.would_update === 1 ? "" : "s"}.
              </p>
              {applied.post_execution_errors?.length > 0 && (
                <>
                  <p className="mt-2 text-sm font-medium text-destructive">
                    {applied.post_execution_errors.length} post-execution
                    errors:
                  </p>
                  <ul className="list-disc pl-6 text-xs text-destructive">
                    {applied.post_execution_errors.map((e, i) => (
                      <li key={i}>
                        Row {e.row}: {e.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-update-close-btn"
          >
            Close
          </Button>
          <Button
            onClick={runApply}
            disabled={!canApply || committing}
            data-testid="bulk-update-apply-btn"
          >
            {committing ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Applying…
              </>
            ) : (
              <>Apply updates</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatCell(v) {
  if (v === null || v === undefined) return <em className="text-muted-foreground">∅</em>;
  if (v === "") return <em className="text-muted-foreground">""</em>;
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}
